import {
  oauthTokenSelector,
  selectAccountSubdomain,
  selectMailboxIds,
} from 'selectors/app/base'

import config from 'config'
import { batchActions } from 'util/redux'
import { isEmpty } from 'util/objects'
import storage from 'util/storage'
import { BEGIN, COMMIT, REVERT } from 'redux-optimist'
import { v4 as uuidV4 } from 'uuid'
import { redirect } from 'redux-first-router'
import graphql from 'api/graphql'
import { statusStates } from 'constants/status_states'
import { doBuildMenuFromWidgets } from 'ducks/folders/operations/collections'
import { selectPrimaryKnowledgeBase } from 'subapps/kb/selectors/knowledge_bases'
import { doFetchKnowledgeBases } from 'subapps/kb/actions/knowledge_bases'
import { SETTINGS_WIDGETS_PAGE } from 'subapps/settings/types'

import {
  createFetchWidgetsRequestAction,
  createFetchWidgetsSuccessAction,
  createFetchWidgetsFailAction,
} from './createFetchWidgets'

import {
  widgetsQuery,
  settingsQuery,
  publishSettingsMutation,
  revertWidgetSettingsMutation,
  updateWidgetSettingsMutation,
  createWidgetMutation,
  updateWidgetMutation,
  deleteWidgetMutation,
  demoWidgetQuery,
} from '../api'

import {
  BOOTSTRAP_WIDGETS,
  FETCH_WIDGET_SETTINGS_REQUEST,
  FETCH_WIDGET_SETTINGS_SUCCESS,
  FETCH_WIDGET_SETTINGS_FAILURE,
  FETCH_WIDGET_SNIPPET_REQUEST,
  FETCH_WIDGET_SNIPPET_SUCCESS,
  FETCH_WIDGET_SNIPPET_FAILURE,
  UPDATE_WIDGET_SETTINGS_REQUEST,
  UPDATE_WIDGET_SETTINGS_SUCCESS,
  UPDATE_WIDGET_SETTINGS_FAILURE,
  PUBLISH_WIDGET_SETTINGS_REQUEST,
  PUBLISH_WIDGET_SETTINGS_SUCCESS,
  PUBLISH_WIDGET_SETTINGS_FAILURE,
  REVERT_WIDGET_SETTINGS_REQUEST,
  REVERT_WIDGET_SETTINGS_SUCCESS,
  REVERT_WIDGET_SETTINGS_FAILURE,
  CREATE_WIDGET_REQUEST,
  CREATE_WIDGET_SUCCESS,
  CREATE_WIDGET_FAILURE,
  UPDATE_WIDGET_REQUEST,
  UPDATE_WIDGET_SUCCESS,
  UPDATE_WIDGET_FAILURE,
  DELETE_WIDGET_REQUEST,
  DELETE_WIDGET_SUCCESS,
  DELETE_WIDGET_FAILURE,
  UPDATE_WIDGET_PREVIEW_MESSAGE,
  CREATE_DRAFT_WIDGET_REQUEST,
  CREATE_DRAFT_WIDGET_SUCCESS,
  UPDATE_DRAFT_WIDGET,
  DESTROY_DRAFT_WIDGET,
} from '../types'

import {
  selectSettings,
  selectCurrentWidgetSettings,
} from '../selectors/settings'
import { selectIsOnCreatePage } from '../selectors/page'
import { selectWidgetsBootstrapped } from '../selectors/status'
import { selectWidgetsIds } from '../selectors/widgets'
import {
  selectDraftWidget,
  selectDraftWidgetSettings,
} from '../selectors/draft'
import { selectSnippet } from '../selectors/snippet'

const normalizeWidgetErrors = e => {
  const errorMessage = {}

  if (e && e.errors && e.errors[0] && e.errors[0].originalError) {
    const error = e.errors[0].originalError

    if (error.name) {
      errorMessage.name = error.name[0]
    }
  }
  return errorMessage
}

export const doFetchSettings = widgetId => {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: FETCH_WIDGET_SETTINGS_REQUEST,
      payload: { widgetId },
    })

    const variables = {
      widgetId,
    }

    return graphql(token, settingsQuery(), variables)
      .then(res => {
        const data = res.json.data
        dispatch({
          type: FETCH_WIDGET_SETTINGS_SUCCESS,
          payload: {
            settings: data.widgetSettings,
            widgetId,
          },
        })
      })
      .catch(err => {
        dispatch({
          type: FETCH_WIDGET_SETTINGS_FAILURE,
          payload: { error: err },
          error: true,
        })
      })
  }
}

export const doFetchSnippet = () => {
  return dispatch => {
    dispatch({
      type: FETCH_WIDGET_SNIPPET_REQUEST,
    })

    // use template variables to replace later for the widget
    fetch(`${config.widgetUrl}/api/snippet`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/javascript',
      },
    })
      .then(response => response.text())
      .then(body => {
        dispatch({
          type: FETCH_WIDGET_SNIPPET_SUCCESS,
          payload: {
            snippet: body,
          },
        })
      })
      .catch(err => {
        dispatch({
          type: FETCH_WIDGET_SNIPPET_FAILURE,
          payload: { error: err },
          error: true,
        })
      })
  }
}

export const doUpdateSettings = (_prefs, id, _opts = {}) => {
  return (dispatch, getState) => {
    const options = {
      commitType: 'BRANCH',
      ..._opts,
    }
    const state = getState()
    const token = oauthTokenSelector(state)
    const tid = uuidV4()
    const widgetId = id || state.location.payload.id

    const prefs = calculateSettings(_prefs || {})

    dispatch({
      type: UPDATE_WIDGET_SETTINGS_REQUEST,
      // Optimistically update settings, so that the preview is immediate
      payload: {
        widgetId,
        options,
        settings: prefs,
      },
      optimist: { type: BEGIN, id: tid },
    })

    const variables = {
      widgetId,
      input: Object.keys(prefs).reduce((acc, k) => {
        // eslint-disable-next-line no-unused-expressions
        typeof prefs[k] === 'object' && prefs[k] !== null
          ? (acc[k] = JSON.stringify(prefs[k]))
          : (acc[k] = prefs[k])
        return acc
      }, {}),
      commitType: options.commitType,
    }

    return graphql(token, updateWidgetSettingsMutation(), variables)
      .then(res => {
        const data = res.json.data

        dispatch({
          type: UPDATE_WIDGET_SETTINGS_SUCCESS,
          payload: {
            widgetId,
            options,
            settings: data.updateWidgetSettings,
          },
          optimist: { type: COMMIT, id: tid },
        })
      })
      .catch(err => {
        dispatch({
          type: UPDATE_WIDGET_SETTINGS_FAILURE,
          payload: { error: err },
          error: true,
          optimist: { type: REVERT, id: tid },
        })
      })
  }
}

export default function doFetchWidgets() {
  return (dispatch, getState) => {
    const state = getState()
    dispatch(createFetchWidgetsRequestAction())

    return graphql(oauthTokenSelector(state), widgetsQuery())
      .then(res => {
        const data = res.json.data
        const widgets = data.widgets.data

        // load data for each widget
        widgets.forEach(({ id }) => dispatch(doFetchSettings(id)))

        dispatch(createFetchWidgetsSuccessAction(res))
      })
      .catch(err => {
        dispatch(createFetchWidgetsFailAction(err))
        throw err
      })
  }
}

export function doPublishSettings() {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const { id } = state.location.payload

    dispatch({
      type: PUBLISH_WIDGET_SETTINGS_REQUEST,
    })

    const variables = {
      widgetId: id,
    }

    return graphql(token, publishSettingsMutation(), variables)
      .then(res => {
        const data = res.json.data

        dispatch({
          type: PUBLISH_WIDGET_SETTINGS_SUCCESS,
          payload: {
            settings: data.publishWidgetSettings,
            widgetId: id,
          },
        })
        dispatch(doBuildMenuFromWidgets({ autoRedirect: false }))
      })
      .catch(err => {
        dispatch({
          type: PUBLISH_WIDGET_SETTINGS_FAILURE,
          payload: { error: err },
          error: true,
        })
      })
  }
}

export function doRevertSettings() {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)
    const { id } = state.location.payload

    dispatch({
      type: REVERT_WIDGET_SETTINGS_REQUEST,
    })

    const variables = {
      widgetId: id,
    }

    return graphql(token, revertWidgetSettingsMutation(), variables)
      .then(res => {
        const data = res.json.data

        dispatch({
          type: REVERT_WIDGET_SETTINGS_SUCCESS,
          payload: {
            settings: data.revertWidgetSettings,
            widgetId: id,
          },
        })
      })
      .catch(err => {
        dispatch({
          type: REVERT_WIDGET_SETTINGS_FAILURE,
          payload: { error: err },
          error: true,
        })
      })
  }
}

export function doCreateWidget(name, settings) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: CREATE_WIDGET_REQUEST,
    })

    const variables = {
      input: {
        name,
      },
    }

    return graphql(token, createWidgetMutation(), variables)
      .then(res => {
        const data = res.json.data
        const widget = data.createWidget
        const { id } = widget
        dispatch({
          type: CREATE_WIDGET_SUCCESS,
          payload: {
            widget,
          },
        })
        return dispatch(doUpdateSettings(settings, id))
      })
      .catch(e => {
        dispatch({
          type: CREATE_WIDGET_FAILURE,
          payload: normalizeWidgetErrors(e),
          error: true,
        })
      })
  }
}

export function doUpdateWidget(widgetId, widget) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: UPDATE_WIDGET_REQUEST,
    })

    const variables = {
      id: widgetId,
      input: widget,
    }

    return graphql(token, updateWidgetMutation(), variables)
      .then(res => {
        const data = res.json.data
        const updatedWidget = data.updateWidget

        dispatch({
          type: UPDATE_WIDGET_SUCCESS,
          payload: {
            widget: updatedWidget,
          },
        })
      })
      .catch(err => {
        dispatch({
          type: UPDATE_WIDGET_FAILURE,
          payload: normalizeWidgetErrors(err),
          error: true,
        })
      })
  }
}

export function doDeleteWidget(widgetId) {
  return (dispatch, getState) => {
    const state = getState()
    const token = oauthTokenSelector(state)

    dispatch({
      type: DELETE_WIDGET_REQUEST,
    })

    const variables = {
      id: widgetId,
    }

    return graphql(token, deleteWidgetMutation(), variables)
      .then(() => {
        dispatch({
          type: DELETE_WIDGET_SUCCESS,
          payload: {
            widgetId,
          },
        })
        dispatch(
          redirect({
            type: SETTINGS_WIDGETS_PAGE,
          })
        )
      })
      .catch(err => {
        dispatch({
          type: DELETE_WIDGET_FAILURE,
          payload: { error: err },
          error: true,
        })
      })
  }
}

export function doBootstrapWidgetsAndSettings() {
  // eslint-disable-next-line func-names
  return function(dispatch, getState) {
    const state = getState()
    const isBootstrapped = selectWidgetsBootstrapped(state)
    // HACK (jscheel): Circular dependency that's not worth dealing with in the
    // locations selectors. Just grab the dumb values here.
    const { id: currentId } = state.location.payload
    const { id: prevId } = (state.location.prev || {}).payload
    if (isBootstrapped && currentId !== prevId) {
      // NOTE (jscheel): Refetch the settings if we are switching widgets.
      const { id } = state.location.payload
      if (id) {
        dispatch(doFetchSettings(id))
      }
    } else if (isBootstrapped) {
      // NOTE (jscheel): It is possible that widgets have been "sort of" bootstrapped
      // by the chat subapp. However, that bootstrapping does not ensure that the
      // settings are fetched. Here we do some cleanup to ensure that the settings
      // and the snippet are also loaded.
      const widgetIds = selectWidgetsIds(state)
      const settings = selectSettings(state)
      widgetIds.forEach(id => {
        if (!settings[id] || settings[id] === statusStates.ERROR) {
          dispatch(doFetchSettings(id))
        }
      })
      const snippet = selectSnippet(state)
      if (!snippet) {
        dispatch(doFetchSnippet())
      }
    } else if (!isBootstrapped) {
      dispatch(
        batchActions(
          { type: BOOTSTRAP_WIDGETS },
          doFetchWidgets(),
          doFetchSnippet()
        )
      )
    }
  }
}

export function doHidePreviewMessage() {
  storage.set('hide-widget-preview-message', true)
  return {
    type: UPDATE_WIDGET_PREVIEW_MESSAGE,
    payload: {
      hide: true,
    },
  }
}

function calculateSettings(settings, options = {}) {
  const newSettings = { ...settings }

  if (isEmpty(newSettings)) return newSettings

  const checks = ['contact', 'kb', 'chat']
  const hasMultiple =
    checks.map(c => settings[`${c}.enabled`]).filter(Boolean).length > 1

  if (options.attemptStartScreenUpgrade && hasMultiple) {
    newSettings.startScreen = 'home'
  } else if (
    (newSettings.startScreen === 'home' && !hasMultiple) ||
    (newSettings.startScreen !== 'home' &&
      !newSettings[`${newSettings.startScreen}.enabled`])
  ) {
    const firstEnabled = checks.find(c => newSettings[`${c}.enabled`])
    newSettings.startScreen = firstEnabled || 'home'
  }

  const offlineAgentEnabledKey = 'chat.skills.offlineAgents.enabled'
  const offlineAgentsEnabled = settings[offlineAgentEnabledKey]

  if (offlineAgentsEnabled) {
    const offlineAgentDisplayOptionKey =
      'chat.skills.offlineAgents.newConversation.displayOption'
    const offlineAgentsDisplayOption = settings[offlineAgentDisplayOptionKey]
    const contactEnabled = settings['contact.enabled']
    const kbEnabled = settings['kb.enabled']

    if (
      (offlineAgentsDisplayOption === 'contact_home' && !contactEnabled) ||
      (offlineAgentsDisplayOption === 'kb_home' && !kbEnabled)
    ) {
      newSettings[offlineAgentEnabledKey] = false
      newSettings[offlineAgentDisplayOptionKey] = 'user_choice_without_chat'
    }
  }

  return newSettings
}

export function doCreateDraftWidget() {
  return (dispatch, getState) => {
    // NOTE (jscheel): We have to ensure that knowledgebases are fetched, so
    // we break this up even though it doesn't make a request anywhere.
    dispatch({
      type: CREATE_DRAFT_WIDGET_REQUEST,
    })

    let state = getState()
    const token = oauthTokenSelector(state)
    const subdomain = selectAccountSubdomain(state)

    const demoPromise = graphql(token, demoWidgetQuery(), {
      id: `demo-${subdomain}`,
    }).then(res => {
      return res.json.data.widget
    })
    const kbPromise = dispatch(doFetchKnowledgeBases())

    return Promise.all([demoPromise, kbPromise]).then(([demoWidget]) => {
      state = getState()
      const mailboxIds = selectMailboxIds(state)
      const primaryKb = selectPrimaryKnowledgeBase(state)
      const settings = { ...demoWidget.publishedSettings }

      if (mailboxIds && mailboxIds.length > 0) {
        settings['contact.id'] = mailboxIds[0]
        settings['contact.enabled'] = true
      }

      if (primaryKb) {
        settings['kb.id'] = primaryKb.id
        settings['kb.enabled'] = true
      }

      dispatch({
        type: CREATE_DRAFT_WIDGET_SUCCESS,
        payload: {
          settings: calculateSettings(settings),
        },
      })
    })
  }
}

export function doUpdateDraftWidget(
  { widgetFields, settings },
  calculateOptions = {}
) {
  return {
    type: UPDATE_DRAFT_WIDGET,
    payload: {
      widgetFields,
      settings: calculateSettings(settings, calculateOptions),
    },
  }
}

export function doCancelWidgetCreate() {
  return dispatch => {
    // NOTE (jscheel): Can't batch actions here, because batchActions kills the
    // location middleware.
    dispatch({
      type: DESTROY_DRAFT_WIDGET,
    })
    dispatch(
      redirect({
        type: SETTINGS_WIDGETS_PAGE,
      })
    )
  }
}

export function doCreateWidgetFromDraft() {
  return (dispatch, getState) => {
    const state = getState()
    const draftWidget = selectDraftWidget(state)
    if (!draftWidget) throw new Error('No draft widget in store')
    const draftWidgetSettings = selectDraftWidgetSettings(state)
    return dispatch(doCreateWidget(draftWidget.name, draftWidgetSettings))
  }
}

export function doPreCalculateStartScreen() {
  return (dispatch, getState) => {
    const state = getState()
    const isOnCreate = selectIsOnCreatePage(state)
    if (isOnCreate) {
      const settings = selectDraftWidgetSettings(state)
      if (settings.startScreen === undefined) {
        dispatch(doUpdateDraftWidget({ settings: { startScreen: 'home' } }))
      }
    } else {
      const settings = selectCurrentWidgetSettings(state)
      if (settings.startScreen === undefined) {
        dispatch(doUpdateSettings({ startScreen: 'home' }))
      }
    }
  }
}
